import React from "react";
import { Box, Button, CircularProgress, Grid, IconButton, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { clearOnboardingApplicationFlags, getPaysafeCCTermsAndConditions, getPmDetails, getRecordAgreementDetails, getUpdateOnboardingApplication, selectOnboardingApplicationState } from "./sagas/pmonboardingSlice";
import { makeStyles } from "@mui/styles";
import { css } from "@emotion/react";
import { HashLoader } from "react-spinners";
import { useStyles } from "../../style/Pages/BasicCompanyInfo";
import EditIcon from "../../icons/EditPinIcon.svg";
import DeleteIcon from "../../icons/delete_Icon.svg";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { AcceptTermsandConditionsDto, OnboardingStepType, UpdateBeneficialOfficerDTO } from "@inosio/payments-sdk";
import TermsDialog from "../../components/TermsDialog";
import { confirmationText, incompleteApplicationtext } from "../shared/util/helpers";

export const useStyle = makeStyles(() => ({
  largeText: {
    fontFamily: 'Open Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '700 !important',
    fontSize: '18px !important',
    lineHeight: '25px !important',
    color: '#14142B !important',
  },
  detailBox: {
    display: "flex", 
    justifyContent: "space-between", 
    alignItems: "center",
    padding: "0 17px 0 14px",
    height: "56px !important",
    borderBottom: "1px solid #F1F0F5",
  },
  detailText: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    fontSize: '18px !important',
    lineHeight: '24px !important',
    color: '#111827 !important',
  },
  statusBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '113px',
  },
  statusText: {
    fontFamily: 'Work Sans !important',
    fontWeight: '500 !important',
    fontSize: '14px !important',
    lineHeight: '24px !important',
    color: '#DBAE39 !important',
  },
  textBox: {
    display: 'flex',
    flexDirection: "column",
  },
  detailLabelText: {
    fontFamily: 'Work Sans !important',
    fontWeight: '400 !important',
    fontSize: '16px !important',
    lineHeight: '22px !important',
    color: '#A8A8A8 !important',
  },
}))

export const ApplicationWizard = () => {
  const classB = useStyles();
  const classes = useStyle();
  const history = useHistory();
  const dispatch = useDispatch();
  const [openPaysafeCC, setOpenPaysafeCC] = React.useState(false);
  const [openConfirmation, setOpenConfirmation] = React.useState(false);
  const [openFinishLaterConfirmation, setOpenFinishLaterConfirmation] = React.useState(false);

  const { pmDetails, onboarding, errorMessage, isSuccess, isFetching, contract } = useSelector(selectOnboardingApplicationState);

  React.useEffect(() => {
    // Clear all onboarding flags
    dispatch({
      type: clearOnboardingApplicationFlags.type,
      payload: {},
    });

    dispatch({ type: getPaysafeCCTermsAndConditions.type });

    // fetch company uuid if it doesnt exist in app context
    !onboarding?.Uuid &&
      dispatch({
        type: getPmDetails.type,
        payload: {},
      });
  }, []);

  React.useEffect(() => {
    // fetch onboarding application if it doesnt exist in app context
    pmDetails.Company?.Uuid &&
      !onboarding?.Uuid &&
      dispatch({
        type: getUpdateOnboardingApplication.type,
        payload: {
          company_uuid: pmDetails.Company.Uuid,
          data: {},
        },
      });
  }, [pmDetails]);

  const handleClickFinishLater = () => {
    setOpenFinishLaterConfirmation(true);
  }

  const disableSubmit = () => {
    const { PrincipalOfficer, IncorporationState, TaxId, CompanyType, PhysicalAddress, MailingAddress, DbaName, LegalName, PhoneNumber, Email, BankAccount } = onboarding; // fecth every known key in updateCompanyOnboardingDto
    const validationPassed = [PrincipalOfficer?.FirstName, PhysicalAddress?.Country, MailingAddress?.Country, IncorporationState, TaxId, CompanyType, DbaName, LegalName, PhoneNumber, Email, BankAccount?.BankName, BankAccount?.BankFiles?.length].every((item) => !!item); // check that they have been populated

    return !validationPassed;
  };

  const [isApplicationSubmitted, setIsApplicationSubmitted] = React.useState(false);
  const handleClickSubmit = () => {
    setOpenPaysafeCC(true);
  }

  const handleClosePaysafeCC = () => {
    setOpenPaysafeCC(false);
    dispatch({
      type: getRecordAgreementDetails.type,
      payload: {
        Version: contract.contractPaysafeCombined.Version,
        Description: contract.contractPaysafeCombined.Description,
      } as AcceptTermsandConditionsDto,
    });

    dispatch({
      type: getUpdateOnboardingApplication.type,
      payload: {
        company_uuid: onboarding?.Uuid,
        data: { CompletedOnboardingStep: OnboardingStepType.PaymentsProcessingApplication },
      },
    });
    setIsApplicationSubmitted(true);
  };

  React.useEffect(() => {
    if (isSuccess) {
      if (isApplicationSubmitted) setOpenConfirmation(true);
      
      // Clear all onboarding flags
      dispatch({
        type: clearOnboardingApplicationFlags.type,
        payload: {},
      });
    }
  }, [isApplicationSubmitted, isSuccess]);

  const handleClosecConfirmation = () => {
    setOpenConfirmation(false);
    setOpenFinishLaterConfirmation(false);
    history.push("/onboarding/wizard");
  };

  const [beneficialOwners, setBeneficialOwners] = React.useState<UpdateBeneficialOfficerDTO[]>([]);
  React.useEffect(() => {
    setBeneficialOwners(onboarding?.BeneficialOwners || []);
  }, [onboarding?.BeneficialOwners]);

  const deleteOwnerData = (uuid: string|undefined) => {
    if (uuid !== undefined) {
      dispatch({
        type: getUpdateOnboardingApplication.type,
        payload: {
          company_uuid: pmDetails.Company.Uuid,
          data: {
            RemovalBeneficialOwnerUuid: uuid
          },
        },
      });
    } else {
      beneficialOwners.pop();
      setBeneficialOwners([...beneficialOwners]);
    }
  }

  const addBeneficialData = () => {
    const newOwner: UpdateBeneficialOfficerDTO = {
      Dob: "",
      FirstName: "",
      LastName: "",
      Ssn: "",
      Email: "",
      PassportIdNumber: "",
      OwnershipPercentage: 0,
    };
    setBeneficialOwners([...beneficialOwners, newOwner]);
  }

  const hideAddMoreBeneficialBtn = beneficialOwners.find(owner => !owner.FirstName);

  const [totalOwnership, setTotalOwnership] = React.useState(0);
  React.useEffect(() => {
    const ownership = (onboarding?.PrincipalOfficer?.OwnershipPercentage || 0) + (onboarding?.BeneficialOwners?.map((benOwner) => benOwner.OwnershipPercentage || 0).reduce((prev, next) => prev + next, 0) || 0);
    setTotalOwnership(ownership);
  }, [onboarding])

  const override = css`
    display: block;
    margin: 100px auto;
  `;

  return (
    <Grid item xs={11} sm={9} lg={8} xl={7.5}>
      {onboarding?.Uuid ? (
        <React.Fragment>
          {errorMessage?.length ? (
            <div className={classB.globalError}>
              <Typography
                style={{
                  fontFamily: "BMWHelvetica",
                  fontStyle: "normal",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "18px",
                  color: "#DB3939",
                }}
              >
                {`Something went wrong. Please try again later or contact support.`}
              </Typography>
            </div>
          ) : (
            <></>
          )}

          <Typography className={classes.largeText}>Payment Processing Application</Typography>
          <Typography className={classes.largeText} sx={{ marginTop: "5px" }}>Status: Not Submitted</Typography>

          <Typography className={classes.largeText} sx={{ margin: "35px 0 20px" }}>Please complete the below details:</Typography>

          <Box className={classes.detailBox} sx={{ borderTop: "1px solid #F1F0F5" }}>
            <Typography className={classes.detailText}>General Company Data</Typography>
            <Box className={classes.statusBox}>
              <IconButton
                sx={{ padding: "5px", marginRight: "14px" }}
                onClick={() => history.push("/onboarding/generalcompanyinfo")}
              >
                <img src={EditIcon} style={{ fontSize: "13px" }} alt="Edit" />
              </IconButton>
              <Typography className={classes.statusText} sx={{ color: !!onboarding?.LegalName ? '#39DB8D !important': '' }}>
                {!!onboarding?.LegalName ? 'Completed': 'Incomplete'}
              </Typography>
            </Box>
          </Box>

          <Box className={classes.detailBox}>
            <Typography className={classes.detailText}>Legal Company Data</Typography>
            <Box className={classes.statusBox}>
              <IconButton
                sx={{ padding: "5px", marginRight: "14px" }}
                onClick={() => history.push("/onboarding/legalcompanyinfo")}
              >
                <img src={EditIcon} style={{ fontSize: "13px" }} alt="Edit" />
              </IconButton>
              <Typography className={classes.statusText} sx={{ color: !!onboarding?.DbaName ? '#39DB8D !important': '' }}>
                {!!onboarding?.DbaName ? 'Completed': 'Incomplete'}
              </Typography>
            </Box>
          </Box>

          <Box className={classes.detailBox}>
            <Typography className={classes.detailText}>Company Bank Account</Typography>
            <Box className={classes.statusBox}>
              <IconButton
                sx={{ padding: "5px", marginRight: "14px" }}
                onClick={() => history.push("/onboarding/companybankaccount")}
              >
                <img src={EditIcon} style={{ fontSize: "13px" }} alt="Edit" />
              </IconButton>
              <Typography className={classes.statusText} sx={{ color: !!onboarding?.BankAccount?.BankName ? '#39DB8D !important': '' }}>
                {!!onboarding?.BankAccount?.BankName && !!onboarding?.BankAccount?.BankFiles?.length ? 'Completed': 'Incomplete'}
              </Typography>
            </Box>
          </Box>

          <Typography className={classes.largeText} sx={{ marginTop: "20px" }}>Please review the</Typography>
          <Typography className={classes.largeText} sx={{ marginBottom: "18px" }}>Know-Your-Customer data:</Typography>

          <Box className={classes.detailBox} sx={{ borderTop: "1px solid #F1F0F5" }}>
            <Box className={classes.textBox}>
              <Typography className={classes.detailLabelText}>Principal</Typography>
              <Typography className={classes.detailText} sx={{ lineHeight: "21px" }}>
                {onboarding?.PrincipalOfficer?.FirstName || 'First name'} {onboarding?.PrincipalOfficer?.LastName || 'Last name'}
              </Typography>
            </Box>
            <Box className={classes.statusBox}>
              <IconButton
                sx={{ padding: "5px", marginRight: "14px" }}
                onClick={() => history.push("/onboarding/principalkycdata")}
              >
                <img src={EditIcon} style={{ fontSize: "13px" }} alt="Edit" />
              </IconButton>
              <Typography className={classes.statusText} sx={{ color: !!onboarding?.PrincipalOfficer?.FirstName ? '#39DB8D !important': '' }}>
                {!!onboarding?.PrincipalOfficer?.FirstName ? 'Completed': 'Incomplete'}
              </Typography>
            </Box>
          </Box>

          {beneficialOwners.map((owner, index) => (
            <Box className={classes.detailBox} key={`Ben${index}`}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton
                  sx={{ padding: "5px", marginLeft: "-5px", marginRight: "11px" }}
                  onClick={() => deleteOwnerData(owner.Uuid)}
                >
                  <img src={DeleteIcon} style={{ fontSize: "13px" }} alt="Delete" />
                </IconButton>
                <Box className={classes.textBox}>
                  <Typography className={classes.detailLabelText}>Owner</Typography>
                  <Typography className={classes.detailText} sx={{ lineHeight: "21px" }}>
                    {owner.FirstName || 'First name'} {owner.LastName || 'Last name'}
                  </Typography>
                </Box>
              </Box>
              <Box className={classes.statusBox}>
                <IconButton
                  sx={{ padding: "5px", marginRight: "14px" }}
                  onClick={() => history.push(`/onboarding/beneficialownerdata/${index+1}`)}
                >
                  <img src={EditIcon} style={{ fontSize: "13px" }} alt="Edit" />
                </IconButton>
                <Typography className={classes.statusText} sx={{ color: !!owner.FirstName ? '#39DB8D !important': '' }}>
                  {!!owner.FirstName ? 'Completed': 'Incomplete'}
                </Typography>
              </Box>
            </Box>
          ))}

          {onboarding?.PrincipalOfficer?.FirstName && !hideAddMoreBeneficialBtn && totalOwnership <= 75 && (
            <Box sx={{ display: "flex", alignItems: "center", marginTop: "16px" }}>
              <IconButton
                sx={{ padding: "5px", marginRight: "6px" }}
                onClick={addBeneficialData}
              >
                <AddCircleIcon sx={{color: "#39A2DB !important", width: 26, height: 26 }} />
              </IconButton>
              <Typography sx={{ 
                fontFamily: 'Work Sans',
                fontStyle: 'normal',
                fontWeight: '400',
                fontSize: '18px',
                lineHeight: '22px',
                color: '#000000',
              }}>
                Add another 25% or more owner
              </Typography>
            </Box>
          )}

          <Box style={{ width: "100%" }} sx={{ display: "flex", justifyContent: "space-between", marginTop: "96px" }}>
            <div className={classB.finishLaterBtnWrapper}>
              <Button type="submit" variant="outlined" className={classB.finishLaterBtn} onClick={handleClickFinishLater}>
                <Typography className={classB.finishLaterBtnText}>{`Save & Finish Later`}</Typography>
              </Button>
            </div>
            <div className={classB.wrapper}>
              <Button type="submit" disabled={disableSubmit()} className={classB.signUpBtn} onClick={handleClickSubmit}>
                <Typography className={classB.signUpBtnText}>{`Submit Application`}</Typography>
              </Button>
              {isApplicationSubmitted && isFetching && <CircularProgress size={26} className={classB.buttonProgress} />}
            </div>
          </Box>

          <TermsDialog open={openPaysafeCC} scroll={true} text={contract.contractPaysafeCombined.Text} title="Paysafe Agreement" handleConfirm={handleClosePaysafeCC} buttonText="Agree" />
          <TermsDialog open={openConfirmation} scroll={false} text={confirmationText} title="Inosio Payment Processing Application Received" handleConfirm={handleClosecConfirmation} buttonText="Got it!" />
          <TermsDialog open={openFinishLaterConfirmation} scroll={true} text={incompleteApplicationtext} title="Inosio Payment Processing Application Received" handleConfirm={handleClosecConfirmation} buttonText="Got it!" />
        </React.Fragment>
      ) : (
        <Box sx={{ width: '100%' }}>
          <HashLoader color={"#D3D3D3"} loading={true} size={150} css={override} />
        </Box>
      )}
    </Grid>
  );
};
